import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { fpColors } from '../../../styles/theme';
import { PrimaryCTA } from '../../../components/PrimaryCTA';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { toNaiveIso } from '../dateForHandoff';
import {
  loadGroupDetail,
  selectGroupDetail,
  selectMemberNameById,
} from '../../sharing/groupsSlice';
import {
  requestHandoff,
  selectCustodyError,
  selectCustodyLoading,
  clearCustodyError,
} from '../custodySlice';
import { loadSentRequests } from '../../borrowRequests/borrowRequestsSlice';
import type { RootState } from '../../../store/store';

/**
 * RequestHandoffSheet — Sprint 4 / Chunk 10B · FE-10.2.
 *
 * Member-pull "Ask to borrow" flow. Inverse of HandOffSheet. The
 * non-owner taps an item in a crew they belong to → modal opens →
 * picks an expected return date + optional message → submits.
 *
 * Server behavior depends on the group's `auto_approve_handoff` flag:
 *   - true  → custody flips immediately, response carries the new
 *             event + auto_approved=true. Toast: "Item is yours,
 *             due {date}".
 *   - false → backend returns auto_approved=false + a request_id.
 *             Owner sees a pending borrow_requests row. Toast:
 *             "Request sent to {owner}, awaiting approval".
 *
 * The viewer is expected to NOT be the owner. The HandOffSheet is the
 * right surface for owner-push handoffs; this is a different button.
 */

interface RequestHandoffSheetProps {
  visible: boolean;
  itemId: number;
  itemName: string;
  /** Group context — required so the server can scope the borrow_requests
   *  row to the right crew + check auto_approve_handoff. */
  groupId: number;
  /** Owner of the item — used for toast copy ("Request sent to Marcus"). */
  ownerUserId?: number | null;
  onClose: () => void;
  /** Called after a successful submit (auto-approved OR pending). */
  onSuccess?: (autoApproved: boolean) => void;
}

const DEFAULT_DAYS = 7;

function formatDateInputValue(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// FE-211 — Tiny self-contained time-ago renderer (mirrors the one in
// PendingRequestCard so the two surfaces produce identical phrasing).
function formatTimeAgo(iso: string | undefined | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

export const RequestHandoffSheet: React.FC<RequestHandoffSheetProps> = ({
  visible,
  itemId,
  itemName,
  groupId,
  ownerUserId,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const groupDetail = useAppSelector(selectGroupDetail(groupId));
  const ownerName = useAppSelector(selectMemberNameById(ownerUserId ?? null));
  const loading = useAppSelector(selectCustodyLoading(itemId));
  const error = useAppSelector(selectCustodyError(itemId));

  // FE-211 — Surface the most recent decline for this item so the
  // requester can adjust their ask (different return date, message
  // explaining urgency, etc.) instead of re-submitting the same payload
  // and getting declined again. We read from `borrowRequests.sentRequests`
  // (loaded on mount) and pick the freshest DENIED row for this item
  // within the last 30 days.
  const priorDecline = useAppSelector((state: RootState) => {
    const rows = (state.borrowRequests?.sentRequests ?? []) as any[];
    if (!rows.length) return null;
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
    const cutoff = Date.now() - THIRTY_DAYS_MS;
    const matches = rows
      .filter(
        (r) =>
          r?.item_id === itemId &&
          (r?.status ?? '').toUpperCase() === 'DENIED' &&
          r?.denied_at &&
          new Date(r.denied_at).getTime() >= cutoff,
      )
      .sort(
        (a, b) =>
          new Date(b.denied_at).getTime() - new Date(a.denied_at).getTime(),
      );
    return matches[0] ?? null;
  });

  const [returnDate, setReturnDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + DEFAULT_DAYS);
    return formatDateInputValue(d);
  });
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (!visible) return;
    if (!groupDetail) {
      dispatch(loadGroupDetail(groupId));
    }
    // FE-211 — Refresh the sent-requests list so the prior-decline
    // banner can fire even when the sheet is opened on a cold session
    // (slice empty until something dispatches `loadSentRequests`).
    dispatch(loadSentRequests());
  }, [visible, groupId, groupDetail, dispatch]);

  useEffect(() => {
    if (visible) {
      setMessage('');
      const d = new Date();
      d.setDate(d.getDate() + DEFAULT_DAYS);
      setReturnDate(formatDateInputValue(d));
      dispatch(clearCustodyError(itemId));
    }
  }, [visible, itemId, dispatch]);

  const autoApprove = groupDetail?.auto_approve_handoff === true;

  const subtitleCopy = useMemo(() => {
    if (autoApprove) return 'Auto-approve is on for this circle';
    return ownerName ? `Owner: ${ownerName}` : 'Owner approval required';
  }, [autoApprove, ownerName]);

  const handleSubmit = async () => {
    if (loading.request) return;
    // BUG-1 workaround — backend's expected_return_date column is
    // tz-naive (Postgres TIMESTAMP WITHOUT TIME ZONE) and chokes on
    // tz-aware ISO strings with a `...Z` suffix. Strip the suffix
    // here via toNaiveIso(). Remove this when the server-side fix
    // ships (filed as BE-210 / FE issue #207).
    const expectedIso = toNaiveIso(
      returnDate ? new Date(`${returnDate}T17:00:00`).toISOString() : null,
    );
    const result = await dispatch(
      requestHandoff({
        itemId,
        request: {
          group_id: groupId,
          expected_return_date: expectedIso,
          message: message.trim() ? message.trim() : null,
        },
      }),
    );

    if (requestHandoff.fulfilled.match(result)) {
      const auto = result.payload.data.auto_approved;
      Toast.show({
        type: 'success',
        text1: auto ? 'Item is yours' : 'Request sent',
        text2: auto
          ? `${itemName} is now in your custody`
          : ownerName
          ? `Awaiting approval from ${ownerName}`
          : 'Awaiting approval',
      });
      onSuccess?.(auto);
      onClose();
    } else {
      Toast.show({
        type: 'error',
        text1: 'Request failed',
        text2: (result.payload as string) ?? 'Please try again.',
      });
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View
          style={styles.sheet}
          accessible
          accessibilityViewIsModal
          accessibilityLabel="Ask to borrow item"
          // @ts-ignore — RN Web only
          role="dialog"
          aria-modal={true}
          testID="request-handoff-sheet"
        >
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>Ask to borrow</Text>
              <Text style={styles.subtitle} numberOfLines={1}>
                {itemName}
              </Text>
            </View>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityLabel="Close"
              accessibilityRole="button"
              style={styles.closeBtn}
            >
              <MaterialIcons name="close" size={22} color={fpColors.onSurface} />
            </Pressable>
          </View>

          <ScrollView style={styles.body} keyboardShouldPersistTaps="handled">
            {/* FE-211 — Prior-decline context. Render only when the latest
                sent request for this item was DENIED within the last 30
                days. Helps the requester adjust their ask instead of
                resubmitting the same payload. */}
            {priorDecline ? (
              <View
                style={[styles.banner, styles.bannerPriorDecline]}
                testID="request-handoff-prior-decline-banner"
              >
                <MaterialIcons
                  name="info-outline"
                  size={18}
                  color={fpColors.error}
                />
                <View style={{ flex: 1 }}>
                  <Text style={styles.priorDeclineHeader} numberOfLines={1}>
                    {ownerName ? `${ownerName} declined this ` : 'Declined '}
                    {formatTimeAgo(priorDecline.denied_at)}
                  </Text>
                  {(() => {
                    const reason =
                      priorDecline.response_message ??
                      priorDecline.denial_message ??
                      priorDecline.decline_reason ??
                      '';
                    if (!reason || typeof reason !== 'string') return null;
                    return (
                      <Text
                        style={styles.priorDeclineReason}
                        numberOfLines={3}
                      >
                        "{reason.trim()}"
                      </Text>
                    );
                  })()}
                </View>
              </View>
            ) : null}

            <View
              style={[
                styles.banner,
                autoApprove && styles.bannerAutoApprove,
              ]}
            >
              <MaterialIcons
                name={autoApprove ? 'flash-on' : 'how-to-reg'}
                size={18}
                color={autoApprove ? fpColors.tertiary : fpColors.primary}
              />
              <Text style={styles.bannerText}>{subtitleCopy}</Text>
            </View>

            <Text style={styles.sectionLabel}>Expected return</Text>
            {Platform.OS === 'web' ? (
              // @ts-ignore — RN Web only
              <input
                type="date"
                value={returnDate}
                onChange={(e: any) => setReturnDate(e.target.value)}
                style={webInputStyle}
                data-testid="request-handoff-return-date"
              />
            ) : (
              <TextInput
                value={returnDate}
                onChangeText={setReturnDate}
                style={styles.textInput}
                placeholder="YYYY-MM-DD"
                placeholderTextColor={fpColors.outline}
              />
            )}

            <Text style={[styles.sectionLabel, { marginTop: 20 }]}>
              Message (optional)
            </Text>
            <TextInput
              value={message}
              onChangeText={setMessage}
              multiline
              numberOfLines={3}
              placeholder="e.g. need it for the weekend job"
              placeholderTextColor={fpColors.outline}
              style={[styles.textInput, styles.textArea]}
              testID="request-handoff-message"
            />

            {error ? (
              <View style={styles.errorBanner}>
                <MaterialIcons name="error-outline" size={18} color={fpColors.error} />
                <Text style={styles.errorText} numberOfLines={2}>
                  {error}
                </Text>
              </View>
            ) : null}
          </ScrollView>

          <View style={styles.footer} testID="request-handoff-submit-wrap">
            <PrimaryCTA
              label={
                loading.request
                  ? 'Sending…'
                  : autoApprove
                  ? 'Take it'
                  : 'Send request'
              }
              onPress={handleSubmit}
              disabled={loading.request}
              loading={loading.request}
              iconRight={null}
              testID="request-handoff-submit"
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

/* ──────────────────────────────────────────────────────────────────────
 * Styles
 * ────────────────────────────────────────────────────────────────────── */

const webInputStyle: any = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 10,
  border: `1px solid ${fpColors.outlineVariant}`,
  fontSize: 15,
  color: fpColors.onSurface,
  background: fpColors.surfaceContainerLowest,
  fontFamily: 'Inter_400Regular',
  boxSizing: 'border-box',
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(28, 23, 33, 0.55)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: fpColors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    // FE-201 — Constrain the sheet so the body ScrollView can flex
    // within a bounded parent. Previously the sheet only had
    // `maxHeight: '90%'` and `flex` wasn't set on the body, so the
    // ScrollView grew to fit its content and shoved the footer
    // ("Send request" button) below the iPhone 13 viewport's fold
    // (390×844, ~760 useable px after URL bar).
    //
    // Now: sheet is a flex column with a bounded max height, body
    // (ScrollView) gets flex:1, footer gets flexShrink:0 so it
    // never collapses. Net result: when the modal content exceeds
    // available height, the BODY scrolls; the footer always stays
    // visible right above the home indicator.
    flexDirection: 'column',
    maxHeight: '90%',
    paddingBottom: Platform.OS === 'ios' ? 24 : 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.outline,
    marginTop: 2,
  },
  closeBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
  },
  body: {
    // FE-201 — flex:1 so the ScrollView fills the sheet between
    // the fixed header + footer instead of growing to fit content.
    // flexShrink:1 + flexBasis:0 cover the corner where RN Web's
    // layout engine doesn't honor flex:1 alone for ScrollView.
    flex: 1,
    flexShrink: 1,
    flexBasis: 0 as any,
    paddingHorizontal: 20,
    paddingTop: 18,
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: fpColors.primaryContainer,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    marginBottom: 18,
  },
  bannerAutoApprove: {
    backgroundColor: fpColors.tertiaryFixed,
  },
  bannerText: {
    flex: 1,
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  // FE-211 — Distinct surface so the prior-decline context doesn't read
  // as the same affirmative banner as auto-approve / awaiting-owner.
  bannerPriorDecline: {
    backgroundColor: fpColors.errorContainer ?? '#FFDAD6',
    alignItems: 'flex-start',
  },
  priorDeclineHeader: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onErrorContainer ?? fpColors.error,
  },
  priorDeclineReason: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onErrorContainer ?? fpColors.error,
    fontStyle: 'italic',
    marginTop: 4,
  },
  sectionLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  textInput: {
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontFamily: 'Inter_400Regular',
    fontSize: 15,
    color: fpColors.onSurface,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  textArea: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: fpColors.errorContainer,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    marginTop: 16,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.error,
  },
  footer: {
    // FE-201 — flexShrink:0 keeps the Send button visible even when
    // the modal content + keyboard would otherwise crowd it out.
    // paddingBottom adds breathing room above the iOS home indicator
    // / android nav bar.
    flexShrink: 0,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 8,
    borderTopWidth: 1,
    borderTopColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surface,
  },
});

export default RequestHandoffSheet;
